import {
	NewAccountStatus,
	expectedAnnualPaymentsVolumeByStoredValue,
	expectedMonthlyPaymentsCountByStoredValue,
	newAccountPayload,
	newAccountsSteps,
} from 'constants/new_accounts.constants';
import { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { format, getDate, getMonth, getYear, isValid } from 'date-fns';
import { newAccountFieldsValidations } from 'constants/new_accounts.constants';
import { timing } from 'constants/ui.constants';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { getCountries, getCurrencies, getIndustries } from 'state/reducers/global.reducer';
import { getAltshulerEmployees } from 'state/reducers/company.reducers';
import { createFormProps, fieldType, option } from 'components/FormCreatorComponent/types';
import FormCreatorComponent, { isValidForm } from 'components/FormCreatorComponent';
import { FormCard, FormCard2ColumnLayout, FormCardLayout, FormCardSingleColumnLayout } from 'theme/elements/Forms';
import { newAccountTypesForms } from 'sections/NewAccounts/forms.types';
import { overrideNewAccountFormsToCreate } from 'sections/NewAccounts/forms.types.create';
import { ButtonComponent } from 'components/ButtonComponent';
import { approveCustomerDetails, getNewAccounts, upsertAccount } from 'services/new_accounts.service';
import { getIsLoading, getNewAccount, setNewAccount, getError } from 'state/reducers/new_accounts.reducers';
import { overrideNewAccountFormsToApprove } from 'sections/NewAccounts/forms.types.approve';
import { validateFields } from 'components/FormCreator/validation';
import { useParams } from 'react-router-dom';
import React from 'react';
import { FadeIn } from 'theme/elements/Content';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { getHelperText } from '../Stepper/helper';
import PaymentCategoryPicker from 'components/PaymentCategoryPicker';
import PaymentCategoriesView from 'components/PaymentCategoriesView';
import { getPaymentCategories, fetchPaymentCategoryTranslations } from 'services/payment-categories.service';
import {
	selectFlattenedCategoryOptions,
	getPaymentCategoriesLoading,
	getPaymentCategoriesTranslationsMap,
} from 'state/reducers/payment-categories.reducer';

const formatExpectedPaymentCountryLabels = (value: unknown, countries: any[]) => {
	if (!Array.isArray(value) || value.length === 0) {
		return 'n/a';
	}

	return value
		.map((id: number) => {
			const country = _.find(countries, { id });
			return country ? country.label : String(id);
		})
		.join(', ');
};

export interface ICustomerDetails {
	handleForward?: any;
	isApproveMode?: boolean;
}

export const CustomerDetails = ({ handleForward, isApproveMode }: ICustomerDetails) => {
	const t = useAppSelector(selectTranslations);
	const dispatch = useAppDispatch();
	const countries = useAppSelector(getCountries);
	const industries = useAppSelector(getIndustries);
	const currencies = useAppSelector(getCurrencies);
	const employees = useAppSelector(getAltshulerEmployees(true));
	const newAccount = useAppSelector(getNewAccount);
	const isLoading = useAppSelector(getIsLoading);
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [initialValidation, setInitialValidation] = useState<Record<string, any>>({});
	const [formState, setFormState] = useState<Record<string, any>>({});
	//const [forms, setForms] = useState<createFormProps[]>();
	const { action = 'create', id } = useParams();
	const approveCustomerDetailsPermission = hasPermission([permissionEnums.APPROVE_ACCOUNT]);
	const upsertAccountPermission = hasPermission([permissionEnums.UPSERT_ACCOUNT]);
	const approveError = useAppSelector(getError);
	// Payment category state - using correct payment-categories.reducer
	const paymentCategoryOptions = useAppSelector(selectFlattenedCategoryOptions);
	const paymentCategoryOptionsLoading = useAppSelector(getPaymentCategoriesLoading);
	const [selectedPaymentCategoryIds, setSelectedPaymentCategoryIds] = useState<string[]>([]);

	// Load payment category options and translations on mount - always fetch to ensure fresh data
	useEffect(() => {
		dispatch(getPaymentCategories() as any);
		dispatch(fetchPaymentCategoryTranslations() as any);
	}, [dispatch]);

	// Initialize selected payment categories: all for new accounts, saved for drafts
	useEffect(() => {
		if (paymentCategoryOptions.length > 0) {
			if (!newAccount?.id) {
				// New account - auto-select all categories
				setSelectedPaymentCategoryIds(paymentCategoryOptions.map((opt) => opt.id));
			} else if (newAccount?.allowedPaymentCategoryIds) {
				// Editing draft - use saved categories, filtered to only active ones
				const availableIds = new Set(paymentCategoryOptions.map((opt) => opt.id));
				const validSavedIds = newAccount.allowedPaymentCategoryIds
					.map(String)
					.filter((id: string) => availableIds.has(id));
				setSelectedPaymentCategoryIds(validSavedIds);
			}
		}
	}, [paymentCategoryOptions, newAccount?.id, newAccount?.allowedPaymentCategoryIds]);

	const optionsDependies = useMemo(() => {
		const options: Record<string, any> = {};
		if (countries) {
			_.assign(options, { countryId: countries }, []);
		}
		if (industries) {
			_.assign(options, { industryId: industries }, []);
		}
		if (currencies) {
			_.assign(options, { expectedPaymentCurrencyId: currencies }, []);
		}
		if (employees) {
			_.assign(options, { leadId: employees?.altshulerLeads }, []);
			_.assign(options, { customerSuccessManagerId: employees?.altshulerManagers }, []);
			_.assign(options, { agentId: employees?.altshulerAgents }, []);
			_.assign(options, { dealerId: employees?.altshulerDealers }, []);
		}
		return options;
	}, [countries, industries, currencies, employees]);

	useEffect(() => {
		if (newAccount) {
			const basicForms = _.get(newAccountTypesForms, newAccountsSteps.CUSTOMER_DETAILS);
			const initialValues = {};
			basicForms.map((form) => {
				form.fieldsGroups.map((group) => {
					group.fields.map((field) => {
						_.assign(initialValues, {
							[field.props.name]: _.get(newAccount, field.props.name, ''),
						});
					});
				});
			});
			if (!_.isNil(newAccount.expectedMonthlyActivityValueIls)) {
				_.assign(initialValues, {
					expectedAnnualPaymentsVolume:
						expectedAnnualPaymentsVolumeByStoredValue[newAccount.expectedMonthlyActivityValueIls],
					expectedMonthlyPaymentsCount:
						expectedMonthlyPaymentsCountByStoredValue[newAccount.expectedMonthlyActivityVolume],
				});
			}
			const incorporationDate = new Date(newAccount.dateOfIncorporation);
			if (newAccount.dateOfIncorporation && isValid(incorporationDate)) {
				_.assign(initialValues, {
					incorporationDay: String(getDate(incorporationDate)),
					incorporationMonth: String(getMonth(incorporationDate)),
					incorporationYear: String(getYear(incorporationDate)),
				});
			}
			setFormValues(initialValues);
		}
	}, [newAccount]);

	const forms = useMemo(() => {
		const basicForms = _.get(newAccountTypesForms, newAccountsSteps.CUSTOMER_DETAILS);
		const override = isApproveMode
			? _.get(overrideNewAccountFormsToApprove, newAccountsSteps.CUSTOMER_DETAILS)
			: _.get(overrideNewAccountFormsToCreate, newAccountsSteps.CUSTOMER_DETAILS);
		const final = isApproveMode ? _.merge({}, override) : _.merge({}, basicForms, override);
		const formsArr = _.map(final, (form) => ({
			...form,
			fieldsGroups: _.map(form.fieldsGroups, (group) => ({
				...group,
				fields: _.map(group.fields, (field) => ({
					...field,
					customValue:
						isApproveMode && field.props.name === 'expectedPaymentCountryIds'
							? (value: unknown) => formatExpectedPaymentCountryLabels(value, countries || [])
							: field.customValue,
					props: {
						...field.props,
						helperText: getHelperText(newAccount, newAccountsSteps.CUSTOMER_DETAILS, field, t),
						options:
							_.isEqual(field.type, fieldType.select) && _.has(optionsDependies, field.props.name)
								? _.get(optionsDependies, field.props.name)
								: field.props?.options || [],
					},
				})),
			})),
		}));
		return formsArr;
	}, [optionsDependies, isApproveMode, countries]);

	useEffect(() => {
		if (formValues) {
			const typeForms = _.get(newAccountTypesForms, newAccountsSteps.CUSTOMER_DETAILS);
			let formsState: Record<string, any> = {};
			typeForms.forEach((form) => {
				const dirtyValues =
					_.has(formState, form.name) && _.has(formState[form.name], 'dirtyFields')
						? _.get(formState[form.name], 'dirtyFields')
						: {};
				formsState = {
					...formsState,
					[form.name]: isValidForm(form.fieldsGroups, { ...formValues, ...dirtyValues }),
				};
			});
			setFormState(formsState);
		}
	}, [formValues]);

	const handlePaymentCategoryChange = (value: string[]) => {
		setSelectedPaymentCategoryIds(value);
	};

	// Validation: at least one payment category must be selected
	const isPaymentCategoryValid = selectedPaymentCategoryIds.length > 0;

	const handleSaveAndForward = () => {
		if (isApproveMode) {
			if (newAccount.status === NewAccountStatus.IN_REVIEW && approveCustomerDetailsPermission && !isLoading) {
				dispatch(approveCustomerDetails(newAccount?.id));
			} else {
				handleForward();
			}
		} else {
			//continue if new account or exist account with dirty values
			const paymentCategoriesChanged =
				JSON.stringify([...selectedPaymentCategoryIds].map(Number).sort()) !==
				JSON.stringify([...(newAccount?.allowedPaymentCategoryIds || [])].sort());
			if (newAccount?.id && !_.some(_.values(formState), { isDirty: true }) && !paymentCategoriesChanged) {
				handleForward();
				return;
			}

			let values = formValues;
			_.values(formState).forEach(
				(prop) =>
					(values =
						prop?.dirtyFields && !_.isEmpty(prop.dirtyFields) ? { ...values, ...prop.dirtyFields } : values)
			);
			let payloadParams = _.pick(values, newAccountPayload);
			payloadParams = _.mapValues(payloadParams, (val: any, key: string) => {
				return !_.has(newAccountFieldsValidations, key) && [undefined, ''].includes(val) ? null : val;
			});
			const accountId = _.has(newAccount, 'id') ? { id: _.get(newAccount, 'id') } : {};
			const dateOfIncorporation = format(
				new Date(
					Number(values.incorporationYear),
					Number(values.incorporationMonth),
					Number(values.incorporationDay),
					12
				),
				timing.DATE_FOR_SERVICE
			);

			// Add selected payment category IDs to payload
			const allowedPaymentCategoryIds = selectedPaymentCategoryIds.map(Number);

			if (upsertAccountPermission) {
				dispatch(
					upsertAccount({
						...payloadParams,
						...accountId,
						dateOfIncorporation,
						allowedPaymentCategoryIds,
					})
				);
			}
		}
	};

	const Components = {
		review: FormCardLayout,
		create: FormCardSingleColumnLayout,
	};

	const CardLayout = action === 'review' ? FormCardLayout : FormCardSingleColumnLayout; //React.createElement(Components[action], {});

	return (
		<FadeIn>
			<Fragment>
				<CardLayout>
					<>
						{forms &&
							forms.map((form, index: number) => {
								return (
									<FormCard key={index} title={t(form.name)}>
										<FormCreatorComponent
											{...form}
											translation={t}
											values={formValues}
											//initialValidation={initialValidation}
											checkInitValidation={false}
											handleChange={(newFormState: Record<string, any>) => {
												//if (formState?.dirtyFields) {
												setFormState({ ...formState, [form.name]: newFormState });
												//dispatch(setNewAccount(formState.values));
												//}
											}}
										/>
									</FormCard>
								);
							})}
						{!isApproveMode && (
							<FormCard
								title={
									t('page.accounts.new_account.customer.payment_categories.title') ||
									'Payment Categories'
								}
							>
								<PaymentCategoryPicker
									value={selectedPaymentCategoryIds}
									onChange={handlePaymentCategoryChange}
									options={paymentCategoryOptions}
									loading={paymentCategoryOptionsLoading}
									noOptions={!paymentCategoryOptionsLoading && paymentCategoryOptions.length === 0}
									hideTitle
									disabled={isApproveMode}
									required
									error={!isPaymentCategoryValid && !paymentCategoryOptionsLoading}
								/>
							</FormCard>
						)}
						{isApproveMode && newAccount?.allowedPaymentCategoryIds?.length > 0 && (
							<PaymentCategoriesView categoryIds={newAccount.allowedPaymentCategoryIds} />
						)}
						<FormCard2ColumnLayout>
							<ButtonComponent
								disabled={
									isLoading || isApproveMode
										? false
										: !_.every(_.values(formState), { isValid: true }) || !isPaymentCategoryValid
								}
								handleClick={(e: Event) => {
									e.preventDefault();
									handleSaveAndForward();
								}}
								text={`${t('button.to_next_step')}>`}
								styleObj={{ borderRadius: '8px', width: '320px', height: '48px', marginTop: '1rem' }}
							/>
						</FormCard2ColumnLayout>
					</>
				</CardLayout>
			</Fragment>
		</FadeIn>
	);
};
